import { useMemo, useState } from 'react';
import { ArrowDownUp, CheckCircle2, ClipboardCopy } from 'lucide-react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import {
  ESCAPE_MODES,
  ESCAPE_MODE_LABEL,
  escapeUnicode,
  unescapeUnicode,
  type EscapeMode,
} from '../lib/unicode';

const SAMPLE_DECODED = '中文 ABC 😀 &foo';

/**
 * `/tools/encode/unicode` — four-mode Unicode escape codec.
 *
 * Two columns (decoded ↔ escaped). Mode pills + an onlyNonAscii
 * toggle drive the escape direction; the unescape direction is
 * auto-detect so users can paste any mix and it just works.
 *
 * Boundary with sibling tools:
 *   - encode/codec   — URL / Base64 / Hex three-in-one (URL is
 *                      `encodeURIComponent`-shaped, not raw %XX)
 *   - encode/html-entities — named entities (&copy; &lt;)
 *   - encode/unicode — JS literal + percent + numeric HTML refs.
 */
export default function UnicodeTool(): JSX.Element {
  const [mode, setMode] = useState<EscapeMode>('js');
  const [onlyNonAscii, setOnlyNonAscii] = useState(true);
  const [decoded, setDecoded] = useState<string>(SAMPLE_DECODED);
  const [escaped, setEscaped] = useState<string>(() =>
    escapeUnicode(SAMPLE_DECODED, 'js', { onlyNonAscii: true }),
  );

  // Track which side last received a user edit so we don't fight
  // them: typing on the decoded side updates escaped, and vice
  // versa. Switching mode re-runs the decoded → escaped direction
  // because that's the visibly mode-dependent side.
  const reEscape = useMemo(
    () => escapeUnicode(decoded, mode, { onlyNonAscii }),
    [decoded, mode, onlyNonAscii],
  );

  const onChangeDecoded = (next: string): void => {
    setDecoded(next);
    setEscaped(escapeUnicode(next, mode, { onlyNonAscii }));
  };
  const onChangeEscaped = (next: string): void => {
    setEscaped(next);
    setDecoded(unescapeUnicode(next));
  };
  const onModeChange = (m: EscapeMode): void => {
    setMode(m);
    setEscaped(escapeUnicode(decoded, m, { onlyNonAscii }));
  };
  const onOnlyNonAsciiChange = (v: boolean): void => {
    setOnlyNonAscii(v);
    setEscaped(escapeUnicode(decoded, mode, { onlyNonAscii: v }));
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="unicode"
        category="encode"
        description="JS 字面量 \\uXXXX / HTML 十进制 &#NN; / HTML 十六进制 &#xNN; / Percent %XX 四模式互转。解码方向自动识别四种 + UTF-16 代理对 + \\xNN，可任意混贴。"
      />

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            转义模式
          </span>
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] text-ink-muted">
            <input
              type="checkbox"
              checked={onlyNonAscii}
              onChange={(e) => onOnlyNonAsciiChange(e.target.checked)}
            />
            仅转义非 ASCII (≥ U+0080)
          </label>
        </header>
        <div className="flex flex-wrap gap-1.5">
          {ESCAPE_MODES.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => onModeChange(m)}
              className={clsx(
                'rounded border px-2 py-0.5 text-[12px] transition',
                m === mode
                  ? 'border-accent bg-accent/10 text-accent'
                  : 'border-line text-ink-muted hover:border-accent/40 hover:text-ink',
              )}
            >
              {ESCAPE_MODE_LABEL[m]}
            </button>
          ))}
        </div>
      </section>

      <section className="grid min-h-0 flex-1 grid-rows-[auto_auto] gap-3 lg:grid-cols-2 lg:grid-rows-1">
        <Pane
          label="原文"
          value={decoded}
          onChange={onChangeDecoded}
          placeholder="原始文本，下方实时显示转义结果"
        />
        <Pane
          label={`转义结果 (${ESCAPE_MODE_LABEL[mode]})`}
          value={escaped}
          onChange={onChangeEscaped}
          placeholder="转义后的字符串；编辑这一侧自动反向解码"
          // If the escaped side is out-of-sync with the live recompute (because
          // the user is editing the escaped side), show a small "different from
          // current mode" hint — we want them to know switching mode would
          // recompute.
          rightHint={escaped !== reEscape ? '当前编辑中（与模式重新计算结果不同）' : undefined}
        />
      </section>
    </div>
  );
}

function Pane({
  label,
  value,
  onChange,
  placeholder,
  rightHint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  rightHint?: string;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 900);
    } catch {
      /* ignore */
    }
  };
  return (
    <div className="card-pad flex min-h-0 flex-col gap-2">
      <header className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-ink-dim">
          {label}
        </span>
        <div className="flex items-center gap-2 text-[11px] text-ink-dim">
          {rightHint && (
            <span className="inline-flex items-center gap-1">
              <ArrowDownUp className="h-3 w-3" />
              {rightHint}
            </span>
          )}
          <span>{value.length} 字符</span>
          <button
            type="button"
            onClick={onCopy}
            disabled={!value}
            className="btn-ghost px-2 py-0.5"
            title="复制"
          >
            {copied ? (
              <>
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                已复制
              </>
            ) : (
              <>
                <ClipboardCopy className="h-3 w-3" />
                复制
              </>
            )}
          </button>
        </div>
      </header>
      <textarea
        className="input min-h-[180px] flex-1 resize-none font-mono text-[13px]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
      />
    </div>
  );
}
